import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { FC, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
  type Counterparty,
  counterpartyGetCounterpartyOptions,
  counterpartyListCounterpartiesOptions,
  counterpartyListCounterpartiesQueryKey,
} from '@column/api';
import { Button, Currency, Dropdown, DropdownOption, Icon } from '@column/column-ui-kit';

import { ModalCounterparty, ModalInternationalCounterparty } from '~/components/Dialog';
import { useDebounce } from '~/hooks/useDebounce';
import { FeatureFlag, useFeatureFlag } from '~/lib/flags';
import {
  type HighlightSuffixCandidate,
  getHighlightedField,
  getHighlightedSuffix,
  renderHighlight,
} from '~/lib/search/highlight';

export type CounterpartyModalType = 'ach' | 'wire' | 'intl-wire' | 'realtime';

// --- Label components ---

const SUFFIX_CANDIDATES: HighlightSuffixCandidate[] = [
  { field: 'BENEFICIARY_EMAIL', label: 'Email' },
  { field: 'BENEFICIARY_PHONE', label: 'Phone' },
  { field: 'LOCAL_ACCOUNT_NUMBER', label: 'Local Acc#' },
  { field: 'LOCAL_BANK_CODE', label: 'Local Bank' },
  { field: 'COUNTERPARTY_ID', label: 'Counterparty ID' },
];

const HighlightedLabel: FC<{ counterparty: Counterparty }> = ({ counterparty }) => {
  const highlightedName = getHighlightedField(counterparty.search_metadata, 'BENEFICIARY_NAME');
  const highlightedDescription = getHighlightedField(counterparty.search_metadata, 'DESCRIPTION');
  const namePart = highlightedName
    ? renderHighlight(highlightedName)
    : counterparty.name || counterparty.wire?.beneficiary_name;
  const descriptionPart = highlightedDescription ? renderHighlight(highlightedDescription) : counterparty.description;

  if (namePart && descriptionPart) {
    return (
      <>
        {namePart} ({descriptionPart})
      </>
    );
  }
  return <>{namePart || descriptionPart || 'Unnamed'}</>;
};

const HighlightedSmall: FC<{ counterparty: Counterparty }> = ({ counterparty }) => {
  const highlightedRouting = getHighlightedField(counterparty.search_metadata, 'ROUTING_NUMBER');
  const highlightedAccount = getHighlightedField(counterparty.search_metadata, 'ACCOUNT_NUMBER');
  const suffix = getHighlightedSuffix(counterparty.search_metadata, SUFFIX_CANDIDATES);
  return (
    <>
      Rtn# {highlightedRouting ? renderHighlight(highlightedRouting) : counterparty.routing_number} / Acc#{' '}
      {highlightedAccount ? renderHighlight(highlightedAccount) : counterparty.account_number}
      {suffix ? (
        <>
          {' · '}
          {suffix.label}: {renderHighlight(suffix.fragment)}
        </>
      ) : null}
    </>
  );
};

export const formatCounterpartyLabel = (cp: Counterparty) =>
  cp.name && cp.description
    ? `${cp.name} (${cp.description})`
    : cp.description || cp.name || cp.wire?.beneficiary_name || 'Unnamed';

const PlainLabel: FC<{ counterparty: Counterparty }> = ({ counterparty }) => (
  <>{formatCounterpartyLabel(counterparty)}</>
);

const CounterpartyOptionLabel: FC<{
  counterparty: Counterparty;
  highlightSearch: boolean;
  hasSearchQuery: boolean;
}> = ({ counterparty, highlightSearch, hasSearchQuery }) => {
  if (hasSearchQuery && highlightSearch && counterparty.search_metadata?.highlights) {
    return <HighlightedLabel counterparty={counterparty} />;
  }
  return <PlainLabel counterparty={counterparty} />;
};

// --- Shared hook ---

interface UseCounterpartyOptionsConfig {
  value?: string | null;
}

export const useCounterpartyOptions = (config: UseCounterpartyOptionsConfig = {}) => {
  const { value } = config;
  const isSearchEnabled = useFeatureFlag(FeatureFlag.EnableCounterpartySearch);
  const [searchText, setSearchText] = useState<string | undefined>();
  const isNumericSearch = searchText ? /^\d+$/.test(searchText) : false;

  const effectiveQuery = useMemo(() => {
    if (isSearchEnabled && searchText) return { query: searchText, deduplicate: true };
    if (!isSearchEnabled && searchText) return { description: searchText };
    return undefined;
  }, [isSearchEnabled, searchText]);

  const counterpartiesQuery = useQuery({
    ...counterpartyListCounterpartiesOptions({ query: effectiveQuery }),
    placeholderData: keepPreviousData,
    enabled: !(!isSearchEnabled && isNumericSearch),
  });

  const accountNumberQuery = useQuery({
    ...counterpartyListCounterpartiesOptions({ query: { account_number: searchText! } }),
    placeholderData: keepPreviousData,
    enabled: !isSearchEnabled && isNumericSearch && Boolean(searchText),
  });

  const routingNumberQuery = useQuery({
    ...counterpartyListCounterpartiesOptions({ query: { routing_number: searchText! } }),
    placeholderData: keepPreviousData,
    enabled: !isSearchEnabled && isNumericSearch && Boolean(searchText),
  });

  const descriptionQuery = useQuery({
    ...counterpartyListCounterpartiesOptions({ query: { description: searchText! } }),
    placeholderData: keepPreviousData,
    enabled: !isSearchEnabled && isNumericSearch && Boolean(searchText),
  });

  const resolvedId = value ?? undefined;

  type ListResponse = { counterparties?: Counterparty[]; has_more?: boolean } | undefined;
  const counterparties = useMemo(() => {
    if (!isSearchEnabled && isNumericSearch) {
      const all = [
        ...((accountNumberQuery.data as ListResponse)?.counterparties ?? []),
        ...((routingNumberQuery.data as ListResponse)?.counterparties ?? []),
        ...((descriptionQuery.data as ListResponse)?.counterparties ?? []),
      ];
      return Array.from(new Map(all.map((cp) => [cp.id, cp])).values());
    }
    return (counterpartiesQuery.data as ListResponse)?.counterparties ?? [];
  }, [
    isSearchEnabled,
    isNumericSearch,
    counterpartiesQuery.data,
    accountNumberQuery.data,
    routingNumberQuery.data,
    descriptionQuery.data,
  ]);

  const activeCounterpartyQuery = useQuery({
    ...counterpartyGetCounterpartyOptions({ path: { counterparty_id: resolvedId! } }),
    enabled: Boolean(resolvedId),
    placeholderData: () => counterparties.find((cp) => cp.id === resolvedId),
  });

  const activeCounterparty = activeCounterpartyQuery.data as Counterparty | undefined;

  const hasSearchQuery = Boolean(isSearchEnabled && searchText);

  const search = useDebounce((query?: string) => setSearchText(query || undefined), 300, []);

  const toOption = useCallback(
    (cp: Counterparty, highlightSearch: boolean): DropdownOption => ({
      label: (
        <CounterpartyOptionLabel counterparty={cp} highlightSearch={highlightSearch} hasSearchQuery={hasSearchQuery} />
      ),
      small:
        hasSearchQuery && highlightSearch && cp.search_metadata?.highlights ? (
          <HighlightedSmall counterparty={cp} />
        ) : (
          `Rtn# ${cp.routing_number} / Acc# ${cp.account_number}`
        ),
      value: cp.id,
    }),
    [hasSearchQuery]
  );

  const options: DropdownOption[] = useMemo(
    () => counterparties.map((cp) => toOption(cp, true)),
    [counterparties, toOption]
  );

  const hiddenOptions = useMemo(() => {
    if (!resolvedId) return [];

    const selected =
      counterparties.find((cp) => cp.id === resolvedId) ||
      (activeCounterparty?.id === resolvedId ? activeCounterparty : undefined);

    return selected ? [toOption(selected, false)] : [];
  }, [activeCounterparty, resolvedId, counterparties, toOption]);

  return {
    options,
    hiddenOptions,
    search,
    hasSearchQuery,
    counterparties,
    activeCounterparty,
  };
};

// --- Styled ---

const CounterpartyFieldRow = styled.div`
  display: flex;
  gap: 8px;
`;

// --- Component ---

export interface CounterpartyDropdownProps {
  value?: string | null;
  onChange: (value: string) => void;
  type?: CounterpartyModalType;
  onCounterpartyCreated?: (counterparty: Counterparty, defaultCurrency?: Currency) => void;
  editValues?: Counterparty;
  onReset?: () => void;
  showEditReset?: boolean;
  fullWidth?: boolean;
  maxWidth?: string;
  hasError?: boolean;
  placeholder?: string;
  isDisabled?: boolean;
  searchLabel?: string;
  preventSelect?: boolean;
}

export const CounterpartyDropdown: FC<CounterpartyDropdownProps> = ({
  value,
  onChange,
  type,
  onCounterpartyCreated,
  editValues,
  onReset,
  showEditReset,
  fullWidth,
  maxWidth,
  hasError,
  placeholder,
  isDisabled,
  searchLabel,
  preventSelect,
}) => {
  const { options, hiddenOptions, search } = useCounterpartyOptions({ value });
  const queryClient = useQueryClient();
  const [isCounterpartyModalOpen, setIsCounterpartyModalOpen] = useState(false);
  const [isIntlCounterpartyModalOpen, setIsIntlCounterpartyModalOpen] = useState(false);
  const [intlModalValues, setIntlModalValues] = useState<Counterparty | undefined>(undefined);

  const handleCounterpartyCreated = useCallback(
    (counterparty: Counterparty, defaultCurrency?: Currency) => {
      setIsCounterpartyModalOpen(false);
      setIsIntlCounterpartyModalOpen(false);
      queryClient.invalidateQueries({ queryKey: counterpartyListCounterpartiesQueryKey() });
      onCounterpartyCreated?.(counterparty, defaultCurrency);
    },
    [onCounterpartyCreated, queryClient]
  );

  const handleNewClick = useCallback(() => {
    if (type === 'intl-wire') {
      setIntlModalValues(undefined);
      setIsIntlCounterpartyModalOpen(true);
    } else {
      setIsCounterpartyModalOpen(true);
    }
  }, [type]);

  const handleEditClick = useCallback(() => {
    setIntlModalValues(editValues);
    setIsIntlCounterpartyModalOpen(true);
  }, [editValues]);

  const showModals = Boolean(type);

  return (
    <>
      <CounterpartyFieldRow>
        <Dropdown
          options={options}
          hiddenOptions={hiddenOptions}
          active={value ?? undefined}
          fullWidth={fullWidth}
          maxWidth={maxWidth}
          emptyMessage="No counterparties found"
          onSearchChange={(query: string) => search(query)}
          onChange={onChange}
          hasError={hasError}
          isDisabled={isDisabled}
          placeholder={placeholder}
          searchLabel={searchLabel}
          preventSelect={preventSelect}
          search
        />
        {showEditReset ? (
          <CounterpartyFieldRow>
            <Button variant="secondary" onClick={handleEditClick} type="button">
              Edit
            </Button>
            <Button variant="secondary" onClick={onReset} type="button">
              Reset
            </Button>
          </CounterpartyFieldRow>
        ) : showModals ? (
          <Button variant="secondary" icon={<Icon.Plus />} onClick={handleNewClick} type="button">
            New
          </Button>
        ) : null}
      </CounterpartyFieldRow>

      {showModals && (
        <>
          <ModalCounterparty
            isOpen={isCounterpartyModalOpen}
            onClose={() => setIsCounterpartyModalOpen(false)}
            callback={handleCounterpartyCreated}
            type={type}
          />
          <ModalInternationalCounterparty
            isOpen={isIntlCounterpartyModalOpen}
            onClose={() => setIsIntlCounterpartyModalOpen(false)}
            values={intlModalValues as never}
            callback={handleCounterpartyCreated as never}
          />
        </>
      )}
    </>
  );
};
